/**
 * The single request boundary for the four Configuration BFF read endpoints.
 *
 * This is also the **only** place the unvalidated cast to a payload type happens
 * (my-info-ui/contracts/fetch-hooks.md). Callers get a typed result; exactly one function knows
 * the payload was never checked at runtime.
 */

export const CONFIGURATION_QUERY_KEY_BASE = "ddc-pages-configuration";

const BASE_PATH = "/landing/nxlambda/configuration";

export interface ConfigurationRequestOptions {
  /** The BFF resource segment — `userinfo`, `accountaccess`, `featureaccess`, `loginhistory`. */
  resource: string;
  userId: string;
  accountId: string;
  /**
   * Extra query params appended after `accountId`. Entries with `undefined` are omitted, so a
   * caller can pass an optional param without branching. Values are URL-encoded.
   */
  params?: Record<string, string | number | undefined>;
}

/**
 * Builds `/landing/nxlambda/configuration/{resource}/{userId}?accountId={accountId}` plus any
 * extra params. `accountId` is always present: the BFF returns 400 without it.
 */
export const buildConfigurationUrl = ({
  resource,
  userId,
  accountId,
  params
}: ConfigurationRequestOptions) => {
  const search = new URLSearchParams({ accountId });
  Object.keys(params ?? {}).forEach(key => {
    const value = params?.[key];
    if (value !== undefined) {
      search.append(key, String(value));
    }
  });
  return `${BASE_PATH}/${resource}/${encodeURIComponent(userId)}?${search.toString()}`;
};

/**
 * Performs the request and throws on any non-2xx so React Query's `isError` drives the tab's
 * failed state. No sentinel, no partial payload — a failure must never render as data
 * (FR-048, SC-010).
 */
export const configurationRequest = async <TPayload>(
  options: ConfigurationRequestOptions
): Promise<TPayload> => {
  const url = buildConfigurationUrl(options);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Configuration request failed for "${options.resource}": ${response.status} ${response.statusText}`
    );
  }

  // The one unvalidated cast. See the file header and interface/ payload-type comments.
  return (await response.json()) as TPayload;
};
